import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  paperShape
} from 'arts/reducers/work';
import {
  selectionShape
} from 'arts/reducers/selection';
import {
  FULL_BLEED, MATTE, FLOAT_DECKLED, FLOAT_STRIGHT, MAT_COLORS_ORDER
} from './mounts';
import Paper from './paper';
import * as AppClasses from '../Application/styles.module.scss';
import * as Classes from './styles.module.scss';
import Header from './header';

/* eslint-disable prefer-destructuring */
const findPaperAndFrame = (data, paperId, frameId) => {
  if (!data || data.length === 0) {
    return [null, null];
  }
  // // this.props.setFrame(nextProps.data[0], nextProps.data[0].options[0]);
  if (!paperId) {
    return [data[0], data[0].options[0]];
  }

  let paper = data.find((p) => p.paperId === paperId);
  if (!paper) {
    paper = data[0];
  }

  let frame = null;
  frame = paper.options.find((f) => f.frameId === frameId);
  if (!frame) {
    frame = paper.options[0];
  }

  return [paper, frame];
};
/* eslint-enable prefer-destructuring */

const groupBy = (xs, key) => xs.reduce((rv, x) => {
  // eslint-disable-next-line no-param-reassign
  rv[x[key]] = rv[x[key]] || [];
  rv[x[key]].push(x);
  return rv;
}, {});

const generateUniqFrames = (options) => {
  const res = [];
  const groups = groupBy(options, 'frameName');
  const order = [MATTE, FULL_BLEED, FLOAT_DECKLED, FLOAT_STRIGHT];
  Object.keys(groups).forEach((k) => {
    const g = groups[k];
    for (let i = 0, l = order.length; i < l; i += 1) {
      const mount = order[i];
      if (mount !== MATTE) {
        const frame = g.find(f => f.mount === mount);
        if (frame) {
          res.push(frame);
          return;
        }
      } else {
        let matteFrames = g.filter(f => f.mount === mount);
        // Sort by MAT_COLORS_ORDER
        matteFrames = matteFrames.sort((a, b) => {
          const aIdx = MAT_COLORS_ORDER.indexOf(a.frameMatColorName);
          const bIdx = MAT_COLORS_ORDER.indexOf(b.frameMatColorName);
          return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
        });
        matteFrames.forEach(frame => {
          res.push(frame);
        });
      }
    }
  });
  return res;
};

const PapersMenu = ({
  data,
  setFrame,
  setNonGlare,
  selection,
  initialPaperId,
  initialFrameId,
  currentPrice,
  currency,
  landscape,
  nonGlare,
  thumbnails,
  showNoFrameOption,
  wallBuilderMode
}) => {
  useEffect(() => {
    const [paper, frame] = findPaperAndFrame(
      data || [],
      initialPaperId,
      initialFrameId
    );
    setFrame(paper, frame);
  }, [data, initialPaperId, initialFrameId, setFrame]);

  const availableFrames = useMemo(() => {
    const res = {};
    data?.forEach((paper) => {
      res[paper.paperId] = generateUniqFrames(paper.options);
    });
    return res;
  }, [data, data?.options]);

  const selectNoFrame = (paper) => {
    setFrame(paper, paper.options[0]);
    setNonGlare(false);
  };

  const selectFirstAvailableFrame = (paper) => {
    const frames = availableFrames[paper.paperId];
    // When user clicks to add frame, prefer White mat color first
    const whiteMatFrame = frames.find(f => f.frameMatColorName === 'White');
    // If no white mat, prefer unmatted frames
    const unmattedFrame = frames.find(f => !f.frameMatColorName || f.frameMat === 0);
    // Otherwise any frame
    const frameToSelect = whiteMatFrame || unmattedFrame || frames[0];
    setFrame(paper, frameToSelect);
    setNonGlare(false);
  };

  const {
    frame: selectedFrame, paper: selectedPaper
  } = useMemo(() => selection || {}, [selection]);

  const { paperId: selectedPaperId } = useMemo(() => selectedPaper || {}, [selectedPaper]);

  const renderHeaders = () => {
    if (!data || data.length === 0) {
      return (
        <>
          <div className={ classnames(Classes.header, AppClasses.skeleton) }>&nbsp;</div>
          <div className={ classnames(Classes.header, AppClasses.skeleton) }>&nbsp;</div>
          <div className={ classnames(Classes.header, AppClasses.skeleton) }>&nbsp;</div>
        </>
      );
    }

    const { isTapestry } = selectedFrame || {};
    return data.map((paper) => (
      <Header
        skipSelectFrame={ isTapestry }
        selectFrame={ () => selectFirstAvailableFrame(paper) }
        selectNoFrame={ () => selectNoFrame(paper) }
        paper={ paper }
        active={ selectedPaperId ? paper.paperId === selectedPaperId : false }
        frameless={ selectedFrame === paper.options[0] }
        currentPrice={ currentPrice }
        currency={ currency }
        landscape={ landscape }
        key={ paper.paperId }
        onClick={ () => selectNoFrame(paper) }
        showNoFrameOption={ showNoFrameOption }
        wallBuilderMode={ wallBuilderMode }
      />
    ));
  };

  return (
    <>
      { wallBuilderMode ? null : <div className={ Classes.heading }>select size:</div> }
      { renderHeaders() }
      {
        (data || []).map((paper) => (
          <Paper
            paper={ paper }
            key={ paper.paperId }
            selectedFrame={ selectedFrame }
            thumbnails={ thumbnails }
            setFrame={ setFrame }
            setNonGlare={ setNonGlare }
            nonGlare={ nonGlare }
            availableFrames={ availableFrames[paper.paperId] }
            selectFirstAvailableFrame={ selectFirstAvailableFrame }
            showNoFrameOption={ showNoFrameOption }
            wallBuilderMode={ wallBuilderMode }
          />
        ))
      }
    </>
  );
};

PapersMenu.propTypes = {
  data: PropTypes.arrayOf(paperShape),
  setFrame: PropTypes.func.isRequired,
  setNonGlare: PropTypes.func.isRequired,
  selection: selectionShape.isRequired,
  resetSelection: PropTypes.func.isRequired,
  initialPaperId: PropTypes.number,
  initialFrameId: PropTypes.number,
  currentPrice: PropTypes.number,
  currency: PropTypes.string,
  landscape: PropTypes.bool,
  nonGlare: PropTypes.bool,
  thumbnails: PropTypes.array,
  showNoFrameOption: PropTypes.bool,
  wallBuilderMode: PropTypes.bool
};

PapersMenu.defaultProps = {
  showNoFrameOption: false
};

export default PapersMenu;
